// Does the wire JSON a draft saved still fit a template? One answer for both callers: restore asks it to
// spot drift (src/webhook/templateDrift.js), and the template switch asks it before carrying the JSON over
// (carryOver.js). Pure: it reads the raw list-row shape (mapTemplate()'s `.details`) and the saved wire
// JSON, nothing else. See ../README.md.
import { countBraceTokens, isFilled } from './validators';

/** A plain object: not null, not a list. */
export const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/** The first component of `type` in a components list (a card's, or the wire JSON's), or undefined. */
export function findComponent(components, type) {
  return (Array.isArray(components) ? components : []).find((component) => component?.type === type);
}

/**
 * A deep copy of plain JSON, or undefined when none can be made (a cycle, a BigInt): wire JSON that won't copy is JSON
 * nobody can use.
 * @param {*} value
 * @returns {*}
 */
export function cloneJson(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return undefined;
  }
}

function holdsTypedValue(node, inParameters) {
  if (Array.isArray(node)) return node.some((item) => holdsTypedValue(item, inParameters));
  if (node && typeof node === 'object') {
    return Object.entries(node).some(([key, child]) => key !== 'type' && holdsTypedValue(child, inParameters || key === 'parameters'));
  }
  return inParameters && (isFilled(node) || Number.isFinite(node));
}

/**
 * Whether the wire JSON holds a value the marketer typed: a non-blank string, or a number (the offer's expiry), anywhere under
 * a `parameters` list: a button's code or suffix, a card's media link, variables and button suffix. Everything else is the
 * template's shape, which an untouched panel reports too (`type`, `index`, `sub_type`, a voice-call component), so a blank
 * COMPLEX, LTO or carousel payload holds nothing. Plain JSON only, not cycle-safe. The testing-only 'meta' carousel shape
 * writes each quick reply's own label as a payload, which reads as typed here.
 * @param {object} [json] - the wire JSON (waTemplateJson)
 * @returns {boolean}
 */
export const wireJsonHoldsValue = (json) => holdsTypedValue(json, false);

// True when a and b hold the same values, ignoring order — dynamic-URL button indexes.
function sameIndexSet(a, b) {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((value) => setA.has(value));
}

/**
 * Per-card { bodyVars, dynamicUrlButtons, mediaFormat, urlKinds } shape of a template's cards. urlKinds holds
 * the urlType of each dynamic-URL button, aligned with dynamicUrlButtons. The saved wire JSON carries no URL
 * kinds, so cardsMatch leaves them out.
 * @param {object[]} [carouselCards] - details.carouselCards, the raw list-row shape
 * @returns {{ bodyVars: number, dynamicUrlButtons: number[], mediaFormat: string, urlKinds: string[] }[]}
 */
export function carouselShapeSignature(carouselCards) {
  const cardsInput = Array.isArray(carouselCards) ? carouselCards : [];
  return cardsInput.map((card) => {
    const components = Array.isArray(card?.components) ? card.components : [];
    const bodyComponent = findComponent(components, 'body');
    const bodyVars = bodyComponent ? countBraceTokens(bodyComponent.text) : 0;

    const buttonsComponent = findComponent(components, 'buttons');
    const buttons = Array.isArray(buttonsComponent?.buttons) ? buttonsComponent.buttons : [];
    const dynamicUrlButtons = [];
    const urlKinds = [];
    buttons.forEach((button, index) => {
      if (button?.type === 'URL' && button?.urlType !== 'STATIC') {
        dynamicUrlButtons.push(index);
        urlKinds.push(button.urlType);
      }
    });

    const mediaFormat = String(findComponent(components, 'header')?.format || '').toUpperCase();

    return { bodyVars, dynamicUrlButtons, mediaFormat, urlKinds };
  });
}

/**
 * Wire-JSON counterpart of carouselShapeSignature: the same shape (but for urlKinds, which the wire does not
 * carry), read off a saved waTemplateJson instead of the (possibly since-changed) live template. Compared side
 * by side, a slot the template added shows on one side and a slot it removed on the other.
 *
 * Handles both wire shapes: a body component present with 0 parameters (library) and one omitted entirely
 * (meta) both read as bodyVars 0. Button sub_type is compared case-insensitively ('url' meta vs 'URL'
 * library); 'quick_reply' (meta only) is not a dynamic-URL button and is left out. The header's media
 * format is its parameter's type, uppercased: 'image' -> 'IMAGE'.
 * @param {object} [waTemplateJson]
 * @returns {{ cardIndex: number, bodyVars: number, dynamicUrlButtons: number[], mediaFormat: string }[]}
 */
export function savedCarouselSignature(waTemplateJson) {
  const topLevel = Array.isArray(waTemplateJson?.components) ? waTemplateJson.components : [];
  const carouselComponent = topLevel.find((component) => component?.type === 'CAROUSEL');
  const wireCards = Array.isArray(carouselComponent?.cards) ? carouselComponent.cards : [];

  return wireCards.map((card, index) => {
    const components = Array.isArray(card?.components) ? card.components : [];
    // Match by the card's own card_index, not array position — parseCarouselTemplateJson follows the same rule.
    const cardIndex = Number.isInteger(card?.card_index) ? card.card_index : index;

    const bodyComponent = findComponent(components, 'body');
    const bodyVars = Array.isArray(bodyComponent?.parameters) ? bodyComponent.parameters.length : 0;

    const dynamicUrlButtons = components
      .filter((component) => component?.type === 'button' && String(component?.sub_type || '').toLowerCase() === 'url')
      .map((component) => component.index);

    const mediaFormat = String(findComponent(components, 'header')?.parameters?.[0]?.type || '').toUpperCase();

    return { cardIndex, bodyVars, dynamicUrlButtons, mediaFormat };
  });
}

/**
 * True when the saved carousel JSON holds exactly the cards the template defines: same count, and per card
 * the same media format, body variable count and dynamic-URL button indexes.
 * @param {object[]} [carouselCards] - details.carouselCards
 * @param {object} [waTemplateJson] - the saved wire JSON
 * @returns {boolean}
 */
export function cardsMatch(carouselCards, waTemplateJson) {
  const expected = carouselShapeSignature(carouselCards);
  const saved = savedCarouselSignature(waTemplateJson);
  if (saved.length !== expected.length) return false;
  return expected.every((card, index) => {
    const savedCard = saved.find((candidate) => candidate.cardIndex === index);
    return !!savedCard
      && savedCard.mediaFormat === card.mediaFormat
      && savedCard.bodyVars === card.bodyVars
      && sameIndexSet(savedCard.dynamicUrlButtons, card.dynamicUrlButtons);
  });
}

/**
 * True when the template's expiry setting agrees with the saved JSON: details.limitedTimeOffer.has_expiration
 * against a limited_time_offer component being there. Dropping a needed one silently loses the countdown, and
 * a stray one shows a countdown the template never asked for. A template that is no offer has no expiry, so
 * JSON with an offer component does not fit it.
 * @param {object} [details] - a mapTemplate() row's `.details`
 * @param {object} [waTemplateJson] - the saved wire JSON
 * @returns {boolean}
 */
export function ltoExpiryMatches(details, waTemplateJson) {
  const components = Array.isArray(waTemplateJson?.components) ? waTemplateJson.components : [];
  return Boolean(details?.limitedTimeOffer?.has_expiration) === components.some((component) => component?.type === 'limited_time_offer');
}
